import React, { useState } from 'react';
import { Flex, Stack, Text } from '@chakra-ui/layout';
import {
  FormControl,
  FormLabel,
  Icon,
  Image,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { Link, useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { FiHelpCircle } from 'react-icons/fi';
import { Input, Button, ColorfulBar } from '~/components';
import logo from '~/assets/logo-service-gray.png';
import { ROUTER_PATHS, STORAGE } from '~/constants';
import { PrivacyPolicyFooter } from '~/components/PrivacyPolicyFooter';

const recoveryPasswordFormSchema = yup.object().shape({
  password: yup
    .string()
    .min(12, 'Mínimo 12 caracteres.')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#&])[A-Za-z\d@#&]{12,}$/, {
      message:
        'A senha deve conter pelo menos 1 letra maiúscula, 1 minúscula, 1 número e 1 caractere especial dentre (@ # &).',
    })
    .typeError('Please provide a valid date'),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref('password'), undefined], 'Senhas devem ser iguais.'),
});

export const RecoveryPassword: React.VFC = () => {
  const { token } = useParams<{ token: string }>();

  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(recoveryPasswordFormSchema),
  });
  const { errors } = formState;
  const [sent, setSent] = useState<Boolean>(false);
  const { location } = useHistory();
  const expired = location.pathname.includes('expired');

  async function handleForgotPassword(formData: { password: string }) {
    setIsLoading(true);
    const auth_token = localStorage.getItem(`${STORAGE}:token`);
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        password: formData.password,
      }),
    };
    const url = `/api/reset-password/confirm/`;
    fetch(url, requestOptions).then(async resp => {
      if (resp.status >= 400) {
        const responseBody = await resp.json();
        let errorMessage = 'Erro ao alterar a senha';
        if (
          typeof responseBody.password !== undefined ||
          typeof responseBody.detail !== undefined
        )
          errorMessage += `: ${
            resp.status === 404
              ? 'A senha já foi redefinida'
              : responseBody.password
          }`;
        setIsLoading(false);
        toast({
          title: errorMessage,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } else setSent(true);
    });
  }

  const handleBack = () => {
    history.replace(ROUTER_PATHS.LOGIN);
  };

  return (
    <Flex w="100vw" h="100vh" flexDir="column">
      <ColorfulBar />
      <Flex justify="center" p="2" pt={16} h="100%">
        <Flex flexDir="column" align="center">
          <Flex flexDir="column" align="center" maxW={300}>
            <Link to={ROUTER_PATHS.LOGIN}>
              <Image src={logo} maxW="200px" mb="12" />
            </Link>
            {expired && !sent && (
              <>
                <Text
                  color="gray.500"
                  fontSize="15px"
                  textAlign="center"
                  fontWeight={600}
                >
                  Sua senha está expirada.
                </Text>
                <Text color="gray.500" fontSize="14px" textAlign="center">
                  Registre uma nova senha.
                </Text>
              </>
            )}
            {!sent ? (
              <Flex flexDir="column">
                <Stack
                  as="form"
                  w={300}
                  gridGap="2"
                  mt="8"
                  onSubmit={handleSubmit(handleForgotPassword)}
                >
                  <FormControl isInvalid={errors.password}>
                    <FormLabel
                      htmlFor="writeUpFile"
                      style={{ fontSize: '14px' }}
                      justifyContent="space-between"
                      display="flex"
                      marginBottom="8px"
                    >
                      Nova senha
                      <Tooltip
                        hasArrow
                        label="A senha deverá ter no mínimo 12 caracteres. Deve conter ao menos 1 letra maiúscula, 
                        1 letra minúscula, 1 número e 1 caractere especial dentre os seguintes (@ # &)."
                        bg="gray"
                        shouldWrapChildren
                      >
                        <Icon as={FiHelpCircle} />
                      </Tooltip>
                    </FormLabel>
                    <Input
                      name="password"
                      type="password"
                      register={register}
                      error={errors.password}
                    />
                  </FormControl>
                  <Input
                    name="password_confirmation"
                    type="password"
                    label="Confirme a senha"
                    register={register}
                    error={errors.password_confirmation}
                  />
                  <Button type="submit" isLoading={isLoading}>
                    Confirmar
                  </Button>
                </Stack>
              </Flex>
            ) : (
              <Flex flexDir="column">
                <Text textAlign="center" color="gray.">
                  Senha alterada com sucesso!
                </Text>

                <Button mt={8} onClick={handleBack}>
                  Voltar para Login
                </Button>
              </Flex>
            )}
          </Flex>
          <PrivacyPolicyFooter isPortal={false} />
        </Flex>
      </Flex>
    </Flex>
  );
};
